import React, { useCallback, useEffect, useState } from 'react';
import { ArrowLeft, Heart, MessageCircle, Clock, ImagePlus, X, Play, Loader2, Crop } from 'lucide-react';
import postService, {
  DartPost,
  getImageUrl,
  formatRelativeTime,
} from '../../services/postService';
import { t } from '../../i18n';
import { appAlert } from '../alerts/appAlert';
import { isVideoFile, mediaOf } from '../../utils/postMedia';
import useVideoDrafts, { VideoSelectionError } from '../../hooks/useVideoDrafts';

import ArrowScrollRow from '../../components/common/ArrowScrollRow';
import BlurredThumb from '../common/BlurredThumb';
import ImageCropperModal from '../ImageCropperModal';
import { blobToBase64 } from '../../utils/cropImage';
const ACCENT = '#3fced1';
const MAX_IMAGES = 10;

/** Bild im Editor: vorhandener Dateiname ODER neue base64-Data-URL */
interface EditImage {
  /** Dateiname des vorhandenen Bildes (behalten) */
  existingName?: string;
  /** data:-URL eines neu gewählten Bildes */
  dataUrl?: string;
  /** Vorhandenes Video dieses Beitrags (Feed-Videos 13.10.2026): bleibt per videoId erhalten */
  existingVideoId?: string;
  existingPoster?: string | null;
  /** Neu hochgeladenes Video (Draft-Key aus useVideoDrafts) */
  draftKey?: string;
}

interface PostEditorProps {
  post: DartPost;
  isOpen: boolean;
  onClose: () => void;
  /** Nach erfolgreichem Speichern (aktualisierter Post) */
  onSaved: (post: DartPost) => void;
}

/**
 * Vollbild-Editor "Beitrag bearbeiten" (Marcel 2026-08-31) — Web-Pendant
 * zum App-PostEditScreen und Marcels Fullscreen-Regel: fixed inset-0 ÜBER
 * der Navbar mit App-Style-Kopfzeile (Back-Pfeil + Titel).
 * Stats + Bilder verwalten (min. 1) + Beschreibung.
 */
const PostEditor: React.FC<PostEditorProps> = ({ post, isOpen, onClose, onSaved }) => {
  const [images, setImages] = useState<EditImage[]>([]);
  const [caption, setCaption] = useState('');
  const [saving, setSaving] = useState(false);
  /** Neu gewähltes Bild im Editor (Marcel 2026-10-20): Klick auf die Kachel, freies Format */
  const [editIndex, setEditIndex] = useState<number | null>(null);

  const onVideoError = useCallback((code: VideoSelectionError, message?: string) => {
    if (code === 'too-long') appAlert.warning(t('web.component.post-composer.video-zu-lang'));
    else if (code === 'too-large') appAlert.warning(t('web.component.post-composer.video-zu-gross'));
    else appAlert.error(message || t('web.component.post-composer.video-upload-fehlgeschlagen'));
  }, []);
  const videos = useVideoDrafts(onVideoError);

  useEffect(() => {
    if (!isOpen) return;
    setImages(mediaOf(post).map((m) => (m.type === 'video' && m.videoId
      ? { existingVideoId: m.videoId, existingPoster: m.poster ?? null }
      : { existingName: m.file })));
    setCaption(post.caption ?? '');
    setSaving(false);
  }, [isOpen, post]);

  if (!isOpen) return null;

  const addFiles = async (files: FileList | null) => {
    if (!files) return;
    const remaining = MAX_IMAGES - images.length;
    const selected = Array.from(files).slice(0, remaining);
    for (const vf of selected.filter((f) => isVideoFile(f))) {
      const key = videos.addFile(vf, (k) => setImages((prev) => prev.filter((img) => img.draftKey !== k)));
      setImages((prev) => [...prev, { draftKey: key }].slice(0, MAX_IMAGES));
    }
    selected
      .filter((f) => !isVideoFile(f))
      .forEach((file) => {
        const reader = new FileReader();
        reader.onload = () => {
          const dataUrl = typeof reader.result === 'string' ? reader.result : null;
          if (dataUrl) setImages((prev) => [...prev, { dataUrl }]);
        };
        reader.readAsDataURL(file);
      });
  };

  const editSrc = editIndex != null ? images[editIndex]?.dataUrl ?? null : null;
  const handleEditDone = async (blob: Blob) => {
    const idx = editIndex;
    try {
      const dataUrl = await blobToBase64(blob, { dataUrl: true });
      if (idx != null) setImages((prev) => prev.map((img, i) => (i === idx && img.dataUrl ? { dataUrl } : img)));
    } finally {
      setEditIndex(null);
    }
  };

  const save = async () => {
    if (saving) return;
    // Rückmeldungen als zentraler Alert (Marcel 2026-10-06)
    if (images.length === 0) {
      appAlert.warning(t('web.component.post-editor.mindestens-ein-bild-ist-pflicht'));
      return;
    }
    if (videos.uploading) {
      appAlert.warning(t('web.component.post-composer.bitte-warten-upload'));
      return;
    }
    setSaving(true);
    // media-Nutzlast (13.10.2026): Bilder (vorhanden/neu) + Videos (vorhanden/neu) in Reihenfolge
    const payload = images.map((img) => {
      if (img.existingVideoId) return { type: 'video' as const, videoId: img.existingVideoId };
      if (img.draftKey) {
        const d = videos.drafts.find((x) => x.key === img.draftKey);
        return d?.videoId ? { type: 'video' as const, videoId: d.videoId } : null;
      }
      const data = img.existingName ?? img.dataUrl;
      return data ? { type: 'image' as const, data } : null;
    }).filter((m): m is NonNullable<typeof m> => m != null);
    const updated = await postService.updatePost(post.id, caption, undefined, payload);
    if (updated) {
      onSaved(updated);
      onClose();
    } else {
      setSaving(false);
      appAlert.error(t('web.component.post-editor.speichern-fehlgeschlagen-bitte-versuche'));
    }
  };

  const stat = (Icon: typeof Heart, value: string, label: string) => (
    <div className="flex flex-1 flex-col items-center gap-1 rounded-xl border border-zinc-800 py-3">
      <Icon size={17} style={{ color: ACCENT }} />
      <span className="text-base font-extrabold text-white">{value}</span>
      <span className="text-[11px] text-gray-500">{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-[60] flex flex-col bg-zinc-950">
      {/* App-Style-Kopfzeile: Back-Pfeil + Titel + Speichern */}
      <div className="flex items-center gap-3 border-b border-zinc-800 px-4 py-3">
        <button
          type="button"
          onClick={onClose}
          aria-label={t('web.component.chats-manager.zurueck')}
          className="rounded-full p-1.5 text-zinc-300 hover:bg-zinc-900"
        >
          <ArrowLeft size={20} />
        </button>
        <h2 className="flex-1 text-base font-bold text-white">{t('web.component.edit-post-modal.beitrag-bearbeiten')}</h2>
        <button
          type="button"
          onClick={save}
          disabled={saving || images.length === 0 || videos.uploading}
          className="rounded-xl px-4 py-1.5 text-sm font-bold text-black disabled:opacity-50"
          style={{ backgroundColor: ACCENT }}
        >
          {saving ? t('web.component.post-editor.speichert') : t('web.component.lobby-form-modal.speichern')}
        </button>
      </div>

      <div className="mx-auto w-full max-w-[600px] flex-1 overflow-y-auto px-4 py-5">
        {/* Stats */}
        <p className="mb-2 text-[13px] font-semibold text-gray-400">{t('web.component.post-editor.statistiken')}</p>
        <div className="mb-6 flex gap-2.5">
          {stat(Heart, String(post.likeCount ?? 0), 'Likes')}
          {stat(MessageCircle, String(post.commentCount ?? 0), 'Kommentare')}
          {stat(Clock, formatRelativeTime(post.createdAt), t('web.component.post-editor.veroeffentlicht'))}
        </div>

        {/* Bilder */}
        <p className="mb-2 text-[13px] font-semibold text-gray-400">
          {t('web.component.post-editor.medien-min-1-max', { MAX_IMAGES })}
        </p>
        <ArrowScrollRow testId="post-images" className="mb-6" scrollClassName="flex gap-2 pb-1">
          {images.map((img, i) => {
            const draft = img.draftKey ? videos.drafts.find((x) => x.key === img.draftKey) : null;
            const isVideo = Boolean(img.existingVideoId || img.draftKey);
            const src = img.existingVideoId
              ? getImageUrl(img.existingPoster ?? null) ?? ''
              : draft?.poster ? getImageUrl(draft.poster) ?? '' : img.existingName ? getImageUrl(img.existingName) ?? '' : img.dataUrl;
            return (
            <div key={img.existingName ?? img.existingVideoId ?? img.draftKey ?? `neu-${i}`} className="relative shrink-0" data-testid={isVideo ? 'editor-video-tile' : undefined}>
              {src && !isVideo && img.dataUrl ? (
                <button type="button" onClick={() => setEditIndex(i)} aria-label={t('web.component.post-composer.bild-bearbeiten-aria', { idx: i + 1 })} className="relative block h-[150px] w-[120px] overflow-hidden rounded-lg focus-visible:outline-none" data-testid="editor-image-edit">
                  <BlurredThumb src={src} className="h-full w-full" />
                  <span className="pointer-events-none absolute bottom-1 right-1 rounded-full bg-black/70 p-1 text-white" aria-hidden="true"><Crop size={12} /></span>
                </button>
              ) : src && !isVideo ? (
                <BlurredThumb src={src} className="h-[150px] w-[120px] rounded-lg" />
              ) : src ? (
                <img src={src} alt="" className="h-[150px] w-[120px] rounded-lg object-cover" />
              ) : (
                <video src={draft?.previewUrl} muted playsInline preload="metadata" className="h-[150px] w-[120px] rounded-lg object-cover" />
              )}
              {isVideo && (
                <span className="absolute inset-0 flex flex-col items-center justify-center gap-1 rounded-lg bg-black/40 text-white">
                  {!draft || draft.status === 'ready' ? <Play size={18} fill="currentColor" /> : <Loader2 size={16} className="animate-spin" />}
                  {draft?.status === 'checking' && <span className="text-[10px] font-semibold">{t('web.component.post-composer.video-wird-geprueft')}</span>}
                  {draft?.status === 'uploading' && <span className="text-[10px] font-semibold">{draft.progress} %</span>}
                </span>
              )}
              <button
                type="button"
                aria-label={t('web.component.post-editor.bild-entfernen')}
                onClick={() => { if (img.draftKey) videos.remove(img.draftKey); setImages((prev) => prev.filter((_, idx) => idx !== i)); }}
                className="absolute right-1 top-1 rounded-full bg-black/65 p-1 text-white"
              >
                <X size={13} />
              </button>
            </div>
            );
          })}
          {images.length < MAX_IMAGES && (
            <label className="flex h-[150px] w-[120px] shrink-0 cursor-pointer flex-col items-center justify-center gap-1.5 rounded-lg border border-zinc-800 text-xs text-zinc-300 hover:border-zinc-700">
              <ImagePlus size={26} style={{ color: ACCENT }} />
              {t('web.component.listing-admins-card.hinzufuegen')}
              <input
                type="file"
                accept="image/*,video/*"
                multiple
                className="hidden"
                onChange={(e) => {
                  void addFiles(e.target.files);
                  e.target.value = '';
                }}
              />
            </label>
          )}
        </ArrowScrollRow>
        <ImageCropperModal open={editSrc != null} imageSrc={editSrc} aspect="free" title={t('web.component.post-composer.bild-zuschneiden')} onCancel={() => setEditIndex(null)} onDone={handleEditDone} />

        {/* Beschreibung */}
        <p className="mb-2 text-[13px] font-semibold text-gray-400">{t('web.component.report-modal.beschreibung')}</p>
        <textarea
          value={caption}
          onChange={(e) => setCaption(e.target.value)}
          maxLength={2200}
          rows={5}
          className="w-full rounded-xl border border-zinc-800 bg-zinc-900 p-3 text-sm text-zinc-200 focus:border-[#3fced1]/60 focus:outline-none"
          placeholder={t('web.component.post-editor.worum-geht-es-in-deinem')}
        />
        <p className="mt-1 text-right text-[11px] text-gray-600">{caption.length}/2200</p>

      </div>
    </div>
  );
};

export default PostEditor;
